import React, { useState, useEffect, useCallback } from 'react';
import rateLimitService from '../services/rateLimitService';

const RateLimitStatus = ({ onLimitChange }) => {
  const [usageInfo, setUsageInfo] = useState(null);
  const [statusMessage, setStatusMessage] = useState(null);

  const updateStatus = useCallback(() => {
    const info = rateLimitService.getUsageInfo();
    const message = rateLimitService.getStatusMessage();
    const limits = rateLimitService.checkLimits();
    
    setUsageInfo(info);
    setStatusMessage(message);
    
    // Notify parent component about limit status
    if (onLimitChange) {
      onLimitChange(!limits.allowed);
    }
  }, [onLimitChange]);

  useEffect(() => {
    updateStatus();
    
    // Update every 10 seconds to refresh countdown timers
    const interval = setInterval(updateStatus, 10000);
    
    return () => clearInterval(interval);
  }, [updateStatus]);

  if (!usageInfo || !statusMessage) {
    return null;
  }

  const getStatusColor = (type) => {
    switch (type) {
      case 'error': return 'text-red-600 bg-red-50 border-red-200';
      case 'warning': return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'success': return 'text-green-600 bg-green-50 border-green-200';
      case 'info': return 'text-blue-600 bg-blue-50 border-blue-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 80) return 'bg-red-500';
    if (percentage >= 60) return 'bg-orange-500';
    if (percentage >= 40) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const hourlyPercentage = (usageInfo.hourly.used / usageInfo.hourly.limit) * 100;

  return (
    <div className="space-y-3">
      {/* Status Message */}
      <div className={`p-4 rounded-lg border ${getStatusColor(statusMessage.type)}`}>
        <div className="space-y-2">
          <div className="text-sm font-medium">
            {statusMessage.message}
          </div>
          {statusMessage.subtitle && (
            <div className="text-xs opacity-80 leading-relaxed">
              {statusMessage.subtitle}
            </div>
          )}
        </div>
      </div>

      {/* Usage Details */}
      <div className="bg-gray-50 p-3 rounded-lg space-y-3">
        {/* Hourly Usage */}
        <div>
          <div className="flex justify-between items-center mb-1">
            <span className="text-xs font-medium text-gray-700">
              Hourly Usage ({usageInfo.hourly.used}/{usageInfo.hourly.limit})
            </span>
            <span className="text-xs text-gray-600">
              {usageInfo.hourly.remaining} remaining
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all duration-300 ${getProgressColor(hourlyPercentage)}`}
              style={{ width: `${Math.min(100, hourlyPercentage)}%` }}
            ></div>
          </div>
          {usageInfo.hourly.resetIn > 0 && (
            <div className="text-xs text-gray-500 mt-1">
              Resets in {rateLimitService.formatTimeRemaining(usageInfo.hourly.resetIn)}
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default RateLimitStatus;